// ==============================================================================
// DASHBOARD ESPECÍFICO - ENTRENADOR
// ==============================================================================

// src/components/dashboard/CoachDashboard.js
import React from 'react';
import { BaseDashboard } from './BaseDashboard';

// Imports de Material-UI
import {
  Avatar,
  Box,
  Typography,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  Divider,
  Paper
} from '@mui/material';

// Imports de iconos
import {
  FitnessCenter as FitnessIcon,
  People as PeopleIcon,
  Today as TodayIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  Speed as SpeedIcon,
  Timer as TimerIcon,
  EmojiEvents as EventsIcon
} from '@mui/icons-material';

// Importar widgets existentes (comentado hasta que estén disponibles)
// import RoleSpecificWidgets from './widgets/RoleSpecificWidgets';

// Widget de bienvenida del entrenador
const CoachWelcomeWidget = ({ user }) => (
  <Box>
    <Box display="flex" alignItems="center" mb={3}>
      <Avatar sx={{ bgcolor: 'success.main', mr: 2, width: 56, height: 56 }}>
        🏃‍♂️
      </Avatar>
      <Box>
        <Typography variant="h5" gutterBottom>
          Coach {user?.displayName || 'Entrenador'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Desarrollo atlético de excelencia
        </Typography>
      </Box>
    </Box>
    
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Button 
          variant="contained" 
          fullWidth 
          size="small" 
          color="success"
          startIcon={<PeopleIcon />}
        >
          Ver Atletas
        </Button>
      </Grid>
      <Grid item xs={6}>
        <Button 
          variant="outlined" 
          fullWidth 
          size="small" 
          color="success"
          startIcon={<AssignmentIcon />}
        >
          Planes
        </Button>
      </Grid>
    </Grid>
  </Box>
);

// Widget de estadísticas de atletas
const CoachAthletesStatsWidget = () => (
  <Box>
    <Box textAlign="center" mb={2}>
      <Typography variant="h3" color="success.main" fontWeight="bold">
        12
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Atletas activos
      </Typography>
      <Chip 
        label="+2 este mes" 
        color="success" 
        size="small"
        variant="outlined"
      />
    </Box>
    
    <Divider sx={{ my: 2 }} />
    
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Progreso general del equipo
      </Typography>
      <LinearProgress 
        variant="determinate" 
        value={78} 
        sx={{ height: 8, borderRadius: 4, mb: 1 }}
        color="success"
      />
      <Box display="flex" justifyContent="space-between">
        <Typography variant="caption" color="text.secondary">
          Meta mensual
        </Typography>
        <Typography variant="caption" color="success.main">
          78%
        </Typography>
      </Box>
    </Box>
  </Box>
);

// Widget de entrenamientos de hoy
const TodayTrainingWidget = () => {
  const trainings = [
    { 
      time: '16:00', 
      group: 'Velocistas', 
      location: 'Pista Principal',
      athletes: 5,
      type: 'Técnica'
    },
    { 
      time: '17:30', 
      group: 'Fondistas', 
      location: 'Circuito',
      athletes: 4,
      type: 'Resistencia'
    },
    { 
      time: '19:00', 
      group: 'Juveniles', 
      location: 'Gimnasio',
      athletes: 8,
      type: 'Fuerza'
    }
  ];

  return (
    <List dense>
      {trainings.map((training, index) => (
        <ListItem key={index} divider={index < trainings.length - 1}>
          <ListItemAvatar>
            <Avatar sx={{ bgcolor: 'success.light', width: 32, height: 32 }}>
              <TimerIcon fontSize="small" />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" fontWeight="medium">
                  {training.time} - {training.group}
                </Typography>
                <Chip 
                  label={`${training.athletes} atletas`}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              </Box>
            }
            secondary={
              <Typography variant="caption" color="text.secondary">
                📍 {training.location} • 🏃‍♂️ {training.type}
              </Typography>
            }
          />
        </ListItem>
      ))}
    </List>
  );
};

// Widget de gestión de atletas (placeholder para reemplazar por tu widget)
const AthleteManagementWidget = () => (
  <Box>
    <Typography variant="h6" gutterBottom color="success.main">
      Gestión de Atletas
    </Typography>
    <Typography variant="body2" color="text.secondary" paragraph>
      Administra la información y progreso de tus atletas.
    </Typography>
    
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Button variant="outlined" fullWidth size="small">
          Agregar Atleta
        </Button>
      </Grid>
      <Grid item xs={6}>
        <Button variant="outlined" fullWidth size="small">
          Ver Todos
        </Button>
      </Grid>
      <Grid item xs={12}>
        <Button variant="text" fullWidth size="small">
          Exportar Datos
        </Button>
      </Grid>
    </Grid>
  </Box>
);

// Widget de planes de entrenamiento (placeholder para reemplazar por tu widget)
const TrainingPlanWidget = () => {
  const plans = [
    { name: 'Plan Velocidad', athletes: 5, active: true },
    { name: 'Plan Resistencia', athletes: 4, active: true },
    { name: 'Plan Técnica', athletes: 3, active: false }
  ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom color="success.main">
        Planes de Entrenamiento
      </Typography>
      
      <List dense>
        {plans.map((plan, index) => (
          <ListItem key={index} divider={index < plans.length - 1}>
            <ListItemText
              primary={
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" fontWeight="medium">
                    {plan.name}
                  </Typography>
                  <Chip 
                    label={plan.active ? 'Activo' : 'Inactivo'}
                    size="small"
                    color={plan.active ? 'success' : 'default'}
                    variant="outlined"
                  />
                </Box>
              }
              secondary={`${plan.athletes} atletas asignados`}
            />
          </ListItem>
        ))}
      </List>
      
      <Button variant="contained" fullWidth size="small" sx={{ mt: 2 }}>
        Crear Nuevo Plan
      </Button>
    </Box>
  );
};

// Widget de progreso de atletas
const AthleteProgressWidget = () => {
  const athletes = [
    { name: 'Ana Martín', progress: 85, specialty: 'Velocidad' },
    { name: 'Carlos Ruiz', progress: 72, specialty: 'Fondo' },
    { name: 'Sofía López', progress: 91, specialty: 'Salto' },
    { name: 'Diego Torres', progress: 68, specialty: 'Velocidad' }
  ];

  return (
    <Box>
      {athletes.map((athlete, index) => (
        <Box key={index} mb={index < athletes.length - 1 ? 3 : 0}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="body2" fontWeight="medium">
              {athlete.name}
            </Typography>
            <Typography variant="caption" color="success.main">
              {athlete.progress}%
            </Typography>
          </Box>
          
          <LinearProgress 
            variant="determinate" 
            value={athlete.progress} 
            sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
            color={athlete.progress >= 80 ? 'success' : 'primary'}
          />
          
          <Typography variant="caption" color="text.secondary">
            {athlete.specialty}
          </Typography>
          
          {index < athletes.length - 1 && <Divider sx={{ mt: 2 }} />}
        </Box>
      ))}
      
      <Button variant="text" fullWidth size="small" sx={{ mt: 2 }}>
        Ver Progreso Detallado
      </Button>
    </Box>
  );
};

// Widget de resumen general
const CoachSummaryWidget = () => (
  <Box>
    <Grid container spacing={2} textAlign="center">
      <Grid item xs={4}>
        <Typography variant="h6" color="success.main" fontWeight="bold">
          25
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Entrenamientos
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography variant="h6" color="primary.main" fontWeight="bold">
          8
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Competencias
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography variant="h6" color="info.main" fontWeight="bold">
          12
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Récords
        </Typography>
      </Grid>
    </Grid>
    
    <Divider sx={{ my: 2 }} />
    
    <Box textAlign="center">
      <Typography variant="h6" color="success.main" gutterBottom>
        Excelente
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Progreso general del equipo
      </Typography>
    </Box>
  </Box>
);

// Componente principal del dashboard del entrenador
export const CoachDashboard = ({ user, onWidgetsChange }) => {
  const coachWidgets = [
    { 
      id: 'coach-welcome', 
      title: null, // Sin título para que se vea más limpio
      size: { xs: 12, md: 8 }, 
      component: CoachWelcomeWidget,
      order: 1
    },
    { 
      id: 'coach-athletes', 
      title: 'Mis Atletas', 
      size: { xs: 12, md: 4 }, 
      component: CoachAthletesStatsWidget,
      order: 2
    },
    { 
      id: 'coach-training-today', 
      title: 'Entrenamientos Hoy', 
      size: { xs: 12, lg: 6 }, 
      component: TodayTrainingWidget,
      order: 3
    },
    { 
      id: 'coach-summary', 
      title: 'Resumen General', 
      size: { xs: 12, lg: 6 }, 
      component: CoachSummaryWidget,
      order: 4
    },
    { 
      id: 'coach-athlete-management', 
      title: 'Gestión de Atletas', 
      size: { xs: 12, lg: 4 }, 
      component: AthleteManagementWidget, // Reemplaza por RoleSpecificWidgets.AthleteManagementWidget cuando esté disponible
      order: 5
    },
    { 
      id: 'coach-training-plans', 
      title: 'Planes de Entrenamiento', 
      size: { xs: 12, lg: 8 }, 
      component: TrainingPlanWidget, // Reemplaza por RoleSpecificWidgets.TrainingPlanWidget cuando esté disponible
      order: 6
    },
    { 
      id: 'coach-progress', 
      title: 'Progreso de Atletas', 
      size: { xs: 12, md: 6 }, 
      component: AthleteProgressWidget,
      order: 7
    }
  ];

  // Acciones personalizadas para el SpeedDial
  const customActions = [
    {
      icon: <PeopleIcon />,
      tooltip: "Gestionar Atletas",
      onClick: () => console.log('Abrir gestión de atletas')
    },
    {
      icon: <AssignmentIcon />,
      tooltip: "Crear Plan de Entrenamiento",
      onClick: () => console.log('Crear nuevo plan')
    },
    {
      icon: <ScheduleIcon />,
      tooltip: "Programar Entrenamiento",
      onClick: () => console.log('Programar entrenamiento')
    },
    {
      icon: <TrendingUpIcon />,
      tooltip: "Ver Progreso General",
      onClick: () => console.log('Ver progreso detallado')
    }
  ];

  return (
    <BaseDashboard
      role="entrenador"
      user={user}
      widgets={coachWidgets}
      title="Panel de Entrenamiento"
      subtitle="Desarrollo y seguimiento integral de atletas"
      roleIcon="🏃‍♂️"
      roleColor="success"
      onWidgetsChange={onWidgetsChange}
      customActions={customActions}
    />
  );
};

export default CoachDashboard;